import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { getEntityData } from "../../../../api/utils";
import "./org-rep.m.css";

import {
  ROUTES,
  HERO_CONTENT,
  DESCRIPTION_CONTENT,
  MEMBER_COMPANIES_CONTENT,
  CTA_CONTENT,
} from "./org-data";

export const OrganisationsRepresentedPage = (props) => {
  const {
    label: { primary: heroLabelPrimary, secondary: heroLabelSecondary },
    headline: heroHeadline,
  } = HERO_CONTENT;

  const {
    highlight: descriptionHighlight,
    text: descriptionText,
  } = DESCRIPTION_CONTENT;

  const {
    title: memberCompaniesTitle,
    description: memberCompaniesDescription,
  } = MEMBER_COMPANIES_CONTENT;

  const {
    title: ctaTitle,
    description: ctaDescription,
    buttonText: ctaButtonText,
  } = CTA_CONTENT;

  const forums = useSelector((state) =>
    get(
      state,
      "qt.config.publisher-attributes.organisations_represented",
      []
    )
  );

  const orgRepImage = useSelector((state) =>
    get(
      state,
      "qt.config.publisher-attributes.static_pages_images.org_rep_image"
    )
  );

  const {
    data: { initialEntityData = null, initialSlug = "" } = {},
  } = props;

  const [activeSlug, setActiveSlug] = useState(initialSlug || "");
  const [forumDataMap, setForumDataMap] = useState(
    initialSlug && initialEntityData
      ? { [initialSlug]: initialEntityData }
      : {}
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!activeSlug && forums.length > 0) {
      setActiveSlug(forums[0].slug);
    }
  }, [forums, activeSlug]);

  const fetchForumData = useCallback(
    async (slug) => {
      if (forumDataMap[slug]) return;

      setLoading(true);
      try {
        const data = await getEntityData(slug);
        const parsed = JSON.parse(
          get(data, "organisations-list", "[]")
        );

        setForumDataMap((prev) => ({
          ...prev,
          [slug]: parsed || [],
        }));
      } catch {
        setForumDataMap((prev) => ({
          ...prev,
          [slug]: [],
        }));
      } finally {
        setLoading(false);
      }
    },
    [forumDataMap]
  );

  useEffect(() => {
    if (!activeSlug) return;
    if (forumDataMap[activeSlug]) return;

    fetchForumData(activeSlug);
  }, [activeSlug, forumDataMap, fetchForumData]);

  const activeOrgGroups = useMemo(
    () => forumDataMap[activeSlug] || [],
    [forumDataMap, activeSlug]
  );

  return (
    <>
      {/* HERO */}
      <div styleName="hero-section">
        <div styleName="hero-image-wrapper">
          <div
            styleName="hero-bg-image"
            style={{ backgroundImage: `url(${orgRepImage})` }}
          />
          <div styleName="hero-content-desktop">
            <div styleName="main-section">
              <p styleName="hero-label">
                <a
                  href={ROUTES.PEER_GROUP_FORUMS}
                  styleName="hero-label-underline"
                >
                  {heroLabelPrimary}
                </a>

                <span> / </span>

                <a
                  href={ROUTES.ORGANISATIONS_REPRESENTED}
                  styleName="hero-label-underline"
                >
                  {heroLabelSecondary}
                </a>
              </p>

              <h1 styleName="hero-headline">{heroHeadline}</h1>
            </div>
          </div>

          <div styleName="hero-gradient" />
        </div>

        <div styleName="hero-mobile-headline">
          <h1 styleName="hero-headline">{heroHeadline}</h1>
        </div>
      </div>

      {/* DESCRIPTION */}
      <div styleName="org-description-section">
        <div styleName="main-section">
          <p styleName="org-description-text">
            <span styleName="org-description-highlight">
              {descriptionHighlight}
            </span>
            {descriptionText}
          </p>
        </div>
      </div>

      {/* MEMBER COMPANIES */}
      <div styleName="member-companies-section">
        <div styleName="main-section">
          <h2 styleName="member-companies-title">
            {memberCompaniesTitle}
          </h2>

          <div styleName="member-companies-separator" />

          <p styleName="member-companies-description">
            {memberCompaniesDescription}
          </p>

          <div styleName="member-forum-buttons">
            {forums.map(({ name, slug }) => (
              <button
                key={slug}
                styleName={
                  slug === activeSlug
                    ? "member-forum-button member-forum-button--active"
                    : "member-forum-button"
                }
                onClick={() => setActiveSlug(slug)}
              >
                {name}
              </button>
            ))}
          </div>

          <div styleName="forum-content-wrapper">
            {loading && <p>Loading…</p>}

            {!loading &&
              activeOrgGroups.map(({ initial, orgs }) => (
                <div key={initial} styleName="org-row">
                  <span styleName="org-initial">{initial}.</span>

                  <span styleName="org-list">
                    {orgs.map((org, i) => (
                      <span key={org} styleName="org-unit">
                        <span styleName="org-item">{org}</span>

                        {i !== orgs.length - 1 && (
                          <span styleName="org-separator">|</span>
                        )}
                      </span>
                    ))}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div styleName="org-cta-section">
        <div styleName="main-section">
          <div styleName="org-cta-box">
            <h2 styleName="org-cta-title">{ctaTitle}</h2>

            <p styleName="org-cta-description">
              {ctaDescription}
            </p>

            <button
              styleName="org-cta-button"
              onClick={() =>
                (window.location.href = ROUTES.PEER_GROUP_FORUMS)
              }
            >
              {ctaButtonText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrganisationsRepresentedPage;